// BUILD-042 cycle 13 - the employee's Onboarding / Offboarding checklist area in HR Administration (one per process kind). Uses the SAME shared layout as the guided-setup
// Onboarding step (checklist/ChecklistWorkArea.tsx): heading + "Manage checklist templates" link + "+ Assign checklist" above the "Assigned checklists" card, the assign panel
// opening beneath the action, a transient confirmation after assigning, and expandable task rows inside the card. Assignment, the server duplicate guard, completion and
// history are the existing AssignChecklistForm / ChecklistAssignmentsPanel behavior; this only limits which templates and assignments each tab lists.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getHrEmployeeLifecycle, listHrChecklistAssignmentsForEmployee, listHrChecklistTemplates, listHrDocumentAssignmentsForEmployee,
  type HrChecklistAssignment, type HrChecklistTemplate, type HrDocumentAssignment,
} from '../../../api/client';
import { AssignChecklistForm } from '../AssignChecklistForm';
import { ChecklistAssignmentsPanel } from '../ChecklistAssignmentsPanel';
import { AssignedChecklistsCard, ChecklistWorkArea } from './ChecklistWorkArea';
import { DeniedNotice, TargetUnavailableNotice } from '../workspace/LoadNotices';
import { loadErrorKey, PERMISSION_LABEL, useSurfaceFlag } from '../workspace/loadState';
import type { ChecklistKind } from './checklistModel';

export function AdminChecklistsSection({ employeeId, employeeName, nameMap, focusTaskId, kind, defaultHireDate }: {
  employeeId: string; employeeName: string; nameMap: Record<string, string | null>; focusTaskId?: string | null; kind: ChecklistKind;
  /** The employee's current-period hire date: the starting value of the onboarding date field (editable). */
  defaultHireDate?: string | null;
}) {
  const [templates, setTemplates] = useState<HrChecklistTemplate[]>([]);
  const [assignments, setAssignments] = useState<HrChecklistAssignment[]>([]);
  // 'loading' until the first answer; 'error' only if that first answer failed (a failed REFRESH keeps what is shown). A failure is never drawn as "none assigned".
  // 'denied' = the server refused with 403; 'target' = the intentionally non-enumerating 404 on an employee-targeted read; neither is ever drawn as "none".
  const [assignmentsState, setAssignmentsState] = useState<'loading' | 'ready' | 'error' | 'denied' | 'target'>('loading');
  const [templatesState, setTemplatesState] = useState<'loading' | 'ready' | 'error' | 'denied'>('loading');
  // This Employee's OWN document assignments (never a cross-Employee list, matching the server's same-Employee check) so a task can be linked to one of them.
  // `documentsUnavailable`: the read failed or was refused: linked tasks then say a document is required without naming it, and no Link control is offered.
  const [documentAssignments, setDocumentAssignments] = useState<HrDocumentAssignment[]>([]);
  const [documentsUnavailable, setDocumentsUnavailable] = useState(false);
  // Offboarding only: the scheduled last working day (when the caller can read the departure), the natural starting value of the date field.
  const [scheduledLastDay, setScheduledLastDay] = useState<string | null>(null);
  // "View the existing checklist" after a duplicate refusal highlights it in the card below (no navigation needed).
  const [localFocus, setLocalFocus] = useState<string | null>(null);
  const mayManageTemplates = useSurfaceFlag('mayManageChecklistTemplates') !== false;
  const mounted = useRef(true);
  // React StrictMode (dev) runs mount -> cleanup -> mount: the flag must be set true again on every mount or every load result is dropped and the card stays on "Loading…".
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const refresh = () => {
    listHrChecklistTemplates().then((d) => { if (mounted.current) { setTemplates(d.templates.filter((t) => !t.retiredAt && t.processKind === kind)); setTemplatesState('ready'); } }).catch((e) => { if (mounted.current) setTemplatesState((s) => (s === 'ready' ? 'ready' : loadErrorKey(e, { gatedList: true }) === 'denied' ? 'denied' : 'error')); });
    listHrChecklistAssignmentsForEmployee(employeeId).then((d) => { if (mounted.current) { setAssignments(d.assignments.filter((a) => a.processKindSnapshot === kind)); setAssignmentsState('ready'); } }).catch((e) => { if (mounted.current) setAssignmentsState((s) => (s === 'ready' ? 'ready' : loadErrorKey(e, { targeted: true }))); });
    listHrDocumentAssignmentsForEmployee(employeeId).then((d) => { if (mounted.current) { setDocumentAssignments(d.assignments); setDocumentsUnavailable(false); } }).catch(() => { if (mounted.current) { setDocumentAssignments([]); setDocumentsUnavailable(true); } });
  };
  useEffect(() => { refresh(); }, [employeeId, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setScheduledLastDay(null);
    if (kind !== 'offboarding') return;
    // A caller without lifecycle authority simply gets no default (404/403): the date stays a required choice. Never an error on this screen.
    getHrEmployeeLifecycle(employeeId).then((d) => { if (mounted.current) setScheduledLastDay(d.employment.departure && !d.employment.departure.cancelledAt ? d.employment.departure.lastWorkingDate : null); }).catch(() => { /* no default */ });
  }, [employeeId, kind]);

  const defaultAnchor = kind === 'onboarding' ? (defaultHireDate ?? undefined) : (scheduledLastDay ?? undefined);
  const ready = assignmentsState === 'ready';
  const canOpen = ready && templatesState === 'ready' && templates.length > 0;
  const openHint = !ready ? 'The checklists must load first.'
    : templatesState === 'denied' ? `Assigning needs the “${PERMISSION_LABEL.employeeChecklists}” permission.`
    : templatesState !== 'ready' ? 'The templates must load first.'
    : templates.length === 0 ? `No ${kind} checklist templates exist yet.` : undefined;

  const manageLink = mayManageTemplates ? (
    <Link
      to="/hr/admin/checklists"
      state={{ returnTo: `/hr/admin/employees/${encodeURIComponent(employeeId)}?section=checklists&tab=${kind}`, employeeName }}
      className="text-xs text-[#176A9B] hover:underline" data-testid="manage-checklist-templates"
    >
      Manage checklist templates
    </Link>
  ) : null;

  return (
    <ChecklistWorkArea
      kind={kind} manageLink={manageLink} canOpen={canOpen} openHint={openHint}
      assignPanel={(api) => (
        <AssignChecklistForm
          employeeId={employeeId} templates={templates} existingAssignments={assignments} kind={kind} defaultAnchorDate={defaultAnchor}
          onAssigned={() => { api.succeeded('Checklist assigned.'); refresh(); }}
          onCancel={api.close}
          onViewExisting={(id) => { api.close(); setLocalFocus(id); }}
        />
      )}
    >
      {templatesState === 'denied' && ready && <div className="mb-3"><DeniedNotice thing="checklist templates" permission={PERMISSION_LABEL.employeeChecklists} testId="checklist-templates-denied" /></div>}
      {templatesState === 'error' && <p className="text-xs text-red-700 mb-3" role="alert">Checklist templates could not be loaded right now, so none can be chosen. <button type="button" className="underline" onClick={refresh}>Try again</button></p>}
      {templatesState === 'ready' && templates.length === 0 && (
        <p className="text-xs text-gray-500 mb-3" data-testid="checklist-no-templates">No {kind} checklist templates exist yet. Use “Manage checklist templates” to create one.</p>
      )}

      <AssignedChecklistsCard kind={kind} empty={ready && assignments.length === 0}>
        {assignmentsState === 'loading' && <div className="text-sm text-gray-400 py-4" data-testid="checklist-loading">Loading…</div>}
        {assignmentsState === 'error' && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="checklist-load-error">
            The {kind} checklist could not be loaded. This is not the same as none being assigned.{' '}
            <button type="button" className="underline" onClick={refresh}>Try again</button>
          </div>
        )}
        {assignmentsState === 'denied' && <DeniedNotice thing="this employee's checklists" permission={PERMISSION_LABEL.employeeChecklists} testId="checklist-denied" />}
        {assignmentsState === 'target' && <TargetUnavailableNotice testId="checklist-target-unavailable" />}
        {ready && assignments.length > 0 && (
          <div data-testid="checklist-assignments">
            <ChecklistAssignmentsPanel
              assignments={assignments} nameMap={nameMap} onChanged={refresh} showHelp={false} assigneeAdmin={{ employeeId }}
              documentAssignments={documentAssignments} documentsUnavailable={documentsUnavailable} focusTaskId={localFocus ?? focusTaskId}
            />
          </div>
        )}
      </AssignedChecklistsCard>
    </ChecklistWorkArea>
  );
}
